'use client';

import {
  type AriaCheckboxGroupItemProps,
  useCheckbox,
  useCheckboxGroupItem,
} from '@react-aria/checkbox';
import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeRefs } from '@react-aria/utils';
import { useToggleState } from '@react-stately/toggle';
import { type ReactNode, useId, useRef } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { FormControlLabel } from '../../internal/components/form/FormControlLabel';
import { filterProps } from '../../internal/utils/filterProps';
import { focusRingStyles } from '../../styles/focusRing';
import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { useCheckboxGroupContext } from '../checkbox-group/CheckboxGroupContext';
import { CoreIcon, type CoreIconProps } from '../core-icon/CoreIcon';

const resolveIconColor = (
  selected: CheckboxProps['isSelected'],
  indeterminate: CheckboxProps['isIndeterminate'],
  color: CheckboxProps['color'],
  disabled: CheckboxProps['isDisabled'],
  isInvalid: CheckboxProps['isInvalid']
): CoreIconProps['color'] => {
  if (disabled) {
    return 'disabled';
  }
  if (selected || indeterminate) {
    return color;
  }
  if (isInvalid) {
    return 'error';
  }
  return color === 'primary' ? 'secondary' : color;
};

const getAriaDescribedBy = (...ids: (string | undefined)[]) => {
  return ids.filter(Boolean).join(' ') || undefined;
};

type Variant = VariantProps<typeof checkboxStyles>;

interface CheckboxSlotProps {
  /** Additional props for the hidden `<input>` element. */
  input?: ComponentProps<object, 'input'>;
}
interface CheckboxBaseProps {
  /**
   * Whether the `Checkbox` is selected (controlled).
   */
  isSelected?: boolean;
  /**
   * Whether the `Checkbox` is selected (uncontrolled).
   */
  defaultSelected?: boolean;
  /**
   * Handler that is called when the `Checkbox`'s selection state changes.
   */
  onChange?: (isSelected: boolean) => void;

  /**
   * Whether the `Checkbox` is indeterminate.
   */
  isIndeterminate?: boolean;
  /**
   * Whether the `Checkbox` is disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether user input is required on the `Checkbox` before form submission.
   */
  isRequired?: boolean;
  /**
   * Whether the `Checkbox` should receive focus on render.
   */
  autoFocus?: boolean;
  /**
   * Whether the `Checkbox` value is invalid.
   */
  isInvalid?: boolean;

  /** Whether the `Checkbox` is in a success state. */
  isSuccess?: boolean;
  /**
   * The `<form>` element to associate the `Checkbox` with. The value of this attribute must be the id of a `<form>` in the same document.
   */
  form?: string;
  /**
   * The value of the `Checkbox`, used when submitting an HTML form.
   */
  value?: string;
  /**
   * The name of the `Checkbox`, used when submitting an HTML form.
   */
  name?: string;
  /**
   * The `width` and `height` of the `Checkbox` and the `font-size` of the `label` and `endLabel` text.
   * @default 'md'
   */
  size?: Variant['size'];
  /**
   * The color of the `Checkbox`.
   * @default 'primary'
   */
  color?: Variant['color'];
  /**
   * The positioning of the `Checkbox` icon in relation to the label text.
   * @default 'left'
   */
  iconPosition?: Variant['iconPosition'];
  /**
   * The vertical alignment of the checkbox icon.
   * @default 'start'
   */
  alignIcon?: Variant['alignIcon'];
  /**
   * Provide the user with context or feedback for the input.
   * Shown below the `Checkbox`.
   */
  description?: string;
  /**
   * The error message to display when `isInvalid` is true. Overrides `description` when present.
   * Shown below the `Checkbox`.
   */
  errorMessage?: string;
  /**
   * Success message shown when `isSuccess` is true. Overrides `description` when present.
   * Shown below the `Checkbox`.
   */
  successMessage?: string;
  /** The label content displayed for the `Checkbox`. When omitted, provide `aria-labelledby` for accessibility. */
  label?: ReactNode;
  /** Content rendered at the end of the label row (e.g., "$5.00/night"). Requires `label` to be set. */
  endLabel?: ReactNode;
  /** Content rendered between the checkbox icon and the label. */
  leftSlot?: ReactNode;
  /**
   * Props for internal sub-elements.
   * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-checkbox--overview#slotprops | slotProps documentation}
   */
  slotProps?: CheckboxSlotProps;
}

const checkboxClasses = {
  root: 'zds-checkbox-root',
  wrapperLabel: 'zds-checkbox-wrapperlabel',
  input: 'zds-checkbox-input',
  iconWrapper: 'zds-checkbox-iconwrapper',
  checkboxIcon: 'zds-checkbox-icon-checkbox',
  checkboxCheckedIcon: 'zds-checkbox-icon-checkboxchecked',
  checkboxIndeterminateIcon: 'zds-checkbox-icon-checkboxindeterminate',
  label: 'zds-checkbox-label',
} as const;

const checkboxStyles = tv({
  slots: {
    root: [checkboxClasses.root, 'flex', 'flex-col'],
    wrapperLabel: [
      checkboxClasses.wrapperLabel,
      'flex',
      'cursor-pointer',
      'items-start',
      'z--checkbox-padding-bottom',
      'z--checkbox-padding-top',
      'z--checkbox-gap',
    ],
    input: [checkboxClasses.input, 'sr-only'],

    iconWrapper: [
      checkboxClasses.iconWrapper,
      'flex',
      'items-center',
      'justify-center',
      'shrink-0',
    ],

    icon: [], // Identifier classes differ per icon state; applied at render.
    label: [checkboxClasses.label],
  },
  variants: {
    iconPosition: {
      left: '',
      right: {
        wrapperLabel: 'justify-between',
        iconWrapper: 'order-1',
      },
    },
    alignIcon: {
      start: '',
      center: {
        iconWrapper: 'self-center',
      },
    },

    size: {
      sm: {
        iconWrapper: ['z--checkbox-sm-height', 'z--checkbox-sm-width'],
      },
      md: {
        iconWrapper: ['z--checkbox-md-height', 'z--checkbox-md-width'],
      },
    },
    color: {
      primary: {
        label: 'text-primary',
      },
      white: {
        label: 'text-static-white-main',
      },
    },

    isDisabled: {
      true: {
        wrapperLabel: 'cursor-not-allowed',
        label: 'disabled-foreground',
      },
    },
  },
});

type CheckboxProps = ComponentProps<CheckboxBaseProps, 'div'>;

type CheckboxComponent = Component<CheckboxBaseProps, 'div'>;

const Checkbox: CheckboxComponent = (props) => {
  const [localProps, checkboxRaProps, hoverRaProps, focusRingRaProps] =
    filterProps(props, ['useCheckbox', 'useHover', 'useFocusRing']);

  // Component-specific props + restLocalProps for the DOM element
  const {
    size: propsSize,
    iconPosition: propsIconPosition,
    alignIcon: propsAlignIcon,
    color: propsColor,
    className,
    isSuccess,
    description,
    errorMessage,
    successMessage,
    label,
    endLabel,
    leftSlot,
    slotProps,
    ref,
    ...restLocalProps
  } = localProps;

  // Hook-claimed props also needed for rendering
  const { isIndeterminate, value } = props;

  const internalInputRef = useRef<HTMLInputElement>(null);

  const textId = useId();
  const labelTextId = useId();
  const endLabelId = useId();

  const { hoverProps, isHovered } = useHover(hoverRaProps);
  const { isFocusVisible, isFocused, focusProps } =
    useFocusRing(focusRingRaProps);

  const {
    state: groupState,
    color: groupColor,
    size: groupSize,
    iconPosition: groupIconPosition,
    alignIcon: groupAlignIcon,
  } = useCheckboxGroupContext();

  const {
    labelProps,
    inputProps,
    isSelected,
    isDisabled,
    isPressed,
    isInvalid,
  } = groupState
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useCheckboxGroupItem(
        {
          // Checkbox is defined as a 'div' component, so `raProps` is inferred as standard HTMLDivElement attributes.
          // However, `useCheckboxGroupItem` expects `AriaCheckboxGroupItemProps`, which has a specific definition that doesn't perfectly overlap with all HTML attributes.
          // We cast here to assert that the filtered props are compatible with the hook's requirements.
          ...(checkboxRaProps as AriaCheckboxGroupItemProps),
          children: label,
          value: value ?? '',
        },
        groupState,
        internalInputRef
      )
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useCheckbox(
        {
          ...checkboxRaProps,
          children: label,
        },
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useToggleState(checkboxRaProps),
        internalInputRef
      );

  const color = propsColor || groupColor || 'primary';
  const size = propsSize || groupSize || 'md';
  const iconPosition = propsIconPosition || groupIconPosition || 'left';
  const alignIcon = propsAlignIcon || groupAlignIcon || 'start';

  const styles = checkboxStyles({
    color,
    size,
    iconPosition,
    alignIcon,
    isDisabled,
  });

  const focusStyles = focusRingStyles({
    color: color === 'white' ? 'static-white' : 'default',
  });

  const iconColor = resolveIconColor(
    isSelected,
    isIndeterminate,
    color,
    isDisabled,
    isInvalid
  );

  // Only scope the accessible name via aria-labelledby when label is set
  // and the consumer hasn't provided their own aria-label or aria-labelledby.
  const shouldScopeLabel =
    label && !props['aria-label'] && !props['aria-labelledby'];

  const hasDescription =
    (isInvalid && errorMessage) || (isSuccess && successMessage) || description;

  return (
    <div
      {...mergeProps(restLocalProps, hoverProps)}
      className={styles.root({ className: [focusStyles.group(), className] })}
      ref={ref}
      data-indeterminate={isIndeterminate || undefined}
      data-disabled={isDisabled || undefined}
      data-required={inputProps['aria-required'] || undefined}
      data-focused={isFocused || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-selected={isSelected || undefined}
      data-pressed={isPressed || undefined}
      data-hovered={isHovered || undefined}
      data-invalid={isInvalid || undefined}
    >
      <label {...labelProps} className={styles.wrapperLabel({ size })}>
        <input
          {...mergeProps(inputProps, focusProps, slotProps?.input ?? {})}
          className={styles.input({ className: slotProps?.input?.className })}
          ref={mergeRefs(internalInputRef, slotProps?.input?.ref)}
          {...(shouldScopeLabel ? { 'aria-labelledby': labelTextId } : {})}
          aria-describedby={getAriaDescribedBy(
            endLabel ? endLabelId : undefined,
            hasDescription ? textId : undefined,
            inputProps['aria-describedby']
          )}
        />

        <span className={styles.iconWrapper()}>
          <span
            className={focusStyles.item({
              className: `inline-flex rounded-sm outline-offset-0 ${!isSelected && !isIndeterminate ? '' : 'hidden'}`,
            })}
          >
            <CoreIcon
              color={iconColor}
              size={size}
              name="checkbox"
              className={styles.icon({
                className: checkboxClasses.checkboxIcon,
              })}
            />
          </span>
          <span
            className={focusStyles.item({
              className: `inline-flex rounded-sm outline-offset-0 ${isSelected && !isIndeterminate ? '' : 'hidden'}`,
            })}
          >
            <CoreIcon
              color={iconColor}
              size={size}
              name="checkboxchecked"
              className={styles.icon({
                className: checkboxClasses.checkboxCheckedIcon,
              })}
            />
          </span>
          <span
            className={focusStyles.item({
              className: `inline-flex rounded-sm outline-offset-0 ${isIndeterminate ? '' : 'hidden'}`,
            })}
          >
            <CoreIcon
              color={iconColor}
              size={size}
              name="checkboxindeterminate"
              className={styles.icon({
                className: checkboxClasses.checkboxIndeterminateIcon,
              })}
            />
          </span>
        </span>
        <FormControlLabel
          labelTextId={shouldScopeLabel ? labelTextId : undefined}
          endLabelId={endLabel ? endLabelId : undefined}
          label={label}
          isRequired={props.isRequired}
          isDisabled={isDisabled}
          leftSlot={leftSlot}
          endLabel={endLabel}
          color={color}
          className={styles.label()}
          descriptionId={textId}
          description={description}
          errorMessage={errorMessage}
          successMessage={successMessage}
          isInvalid={isInvalid}
          isSuccess={isSuccess}
          size={size}
          alignIcon={alignIcon}
        />
      </label>
    </div>
  );
};

Checkbox.displayName = 'Checkbox';

export {
  Checkbox,
  type CheckboxProps,
  type CheckboxBaseProps,
  checkboxClasses,
};
